/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useState, useContext, useCallback } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { CommentEditor as AkCommentEditor } from '@atlaskit/editor-core/appearance-editor-comment';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	CommentType,
	useCommentsDataActions,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useCreateObjectCommentMutation } from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import {
	REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import type { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import {
	onSaveReply,
	getAdfForQuickReplyMention,
	type SaveProps,
} from '@confluence/comments-panel/entry-points/commentThreadHelper.ts';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import { fg } from '@confluence/feature-gating';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import type {
	ObjectCommentReplyEditorCreateGeneralReplyMutation as ObjectCommentReplyEditorCreateGeneralReplyMutation$data,
	ObjectCommentReplyEditorCreateGeneralReplyMutationVariables as ObjectCommentReplyEditorCreateGeneralReplyMutation$variables,
} from './__types__/ObjectCommentReplyEditorCreateGeneralReplyMutation.ts';

const ObjectCommentReplyEditorCreateGeneralReplyMutation = gql`
	mutation ObjectCommentReplyEditorCreateGeneralReplyMutation(
		$cloudId: ID!
		$input: CreateCommentInput!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;
const commentEditorWrapperStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const commentEditorCustomStyles = {
	marginLeft: token('space.300'),
	// Be careful updating this, it can cause the branch to jump slightly when switching from the button to the editor
	height: '24px',
	marginRight: token('space.050'),
};

export const ObjectCommentReplyEditor = ({
	pageInfo,
	pageMode,
	setReplyView,
	parentComment,
	threadKey,
	replyToId,
	isOffline,
	customBeforeLeftForNestedReply,
}: {
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	setReplyView: (view: ViewReplyOptions) => void;
	parentComment: CommentData;
	threadKey: string;
	replyToId: string;
	isOffline?: boolean;
	customBeforeLeftForNestedReply?: boolean;
}) => {
	const replyEditorContainerId = `object-comment-reply-editor-${replyToId}`;

	const [createGeneralReplyFn] = useMutationInterop<
		ObjectCommentReplyEditorCreateGeneralReplyMutation$data,
		ObjectCommentReplyEditorCreateGeneralReplyMutation$variables
	>(
		// @ts-expect-error -- http://go/cfe-use-relay-artifacts
		ObjectCommentReplyEditorCreateGeneralReplyMutation,
	);

	const pageId = pageInfo?.id ?? '';
	const pageType = pageInfo?.type ?? '';
	const spaceId = pageInfo?.space?.id ?? '';
	const commentType = parentComment.type;

	const { addCommentInCache } = useAllCommentsData({
		contentId: pageId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});
	const createObjectCommentMutationFn = useCreateObjectCommentMutation();

	const { isReactionsEnabled } = useContext(ReactionsContext);

	const { onChange, resetContentChanged } = useCommentsContentActions();

	const { cloudId } = useSessionData();
	const [{ searchSessionId }] = useSearchSessionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addReplyToCommentThread } = useCommentsDataActions();
	const { updateReadCommentsListState } = useUnreadCommentsActions();
	const [{ replyToCommentId, replyToCommentUser }, { setReplyToCommentId }] = useCommentsPanel();

	const [, setCommentForEdit] = useState('');

	const experienceTracker = useContext(ExperienceTrackerContext);

	// User page permissions
	const operations = pageInfo?.operations || [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	// The user can upload media only if they have update permissions for the page if confluence_comment_use_comment_media_endpoint FG is off
	const hasMediaUploadPermissions = fg('confluence_comment_use_comment_media_endpoint')
		? canAddComment
		: operations.some((op) => op?.operation === 'update' && op?.targetType === pageType);

	const onEditorReady = useCallback(() => {
		let experienceName = REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE;

		if (commentType === CommentType.GENERAL) {
			experienceName = REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE;
		}

		experienceTracker.succeed({
			name: experienceName,
		});

		const commentReplyContainer = document.getElementById(replyEditorContainerId);

		if (!commentReplyContainer) {
			return;
		}

		// Check whether the bottom of the comment reply container is visible
		const isBottomOutOfView =
			commentReplyContainer.getBoundingClientRect().bottom > window.innerHeight;

		// Only scroll if we currently can't see the bottom of the comment reply container. It's annoying to always scroll
		if (isBottomOutOfView) {
			commentReplyContainer.scrollIntoView({
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest',
			});
		}
	}, [commentType, experienceTracker, replyEditorContainerId]);

	const handleCancelComment = useCallback(
		({ destroy = false }: { destroy?: boolean } = {}) => {
			if (
				(!destroy || replyToCommentId !== replyToId) &&
				fg('confluence_comment_fix_reply_discard')
			) {
				setReplyToCommentId();
			} else if (!fg('confluence_comment_fix_reply_discard')) {
				setReplyToCommentId();
			}
		},
		[replyToCommentId, replyToId, setReplyToCommentId],
	);

	const handleSaveComment = useCallback(
		(adf: any, onSuccess: any) => {
			return onSaveReply({
				pageId,
				parentCommentId: replyToId,
				cloudId,
				adf,
				onSuccess,
				pageMode,
				setCommentForEdit,
				createAnalyticsEvent,
				pageType,
				experienceTracker,
				createFooterReplyFn: createGeneralReplyFn as SaveProps['createFooterReplyFn'],
				createObjectReplyFn: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
					? createObjectCommentMutationFn
					: undefined,
				addCommentInCommentsDataManagerCacheFn: expVal(
					'cc_comments_new_comments_data_manager',
					'isEnabled',
					false,
				)
					? addCommentInCache
					: undefined,
				addReplyToCommentThread,
				threadKey,
				updateReadCommentsListState,
				commentType,
				searchSessionId,
				isReactionsEnabled,
				setReplyView,
				setReplyToCommentId,
				resetContentChanged,
				analyticsSource: AnalyticsSource.PAGE_FOOTER,
				isFooterComment: true,
			});
		},
		[
			updateReadCommentsListState,
			threadKey,
			setReplyToCommentId,
			setReplyView,
			searchSessionId,
			resetContentChanged,
			replyToId,
			addReplyToCommentThread,
			cloudId,
			commentType,
			createAnalyticsEvent,
			createGeneralReplyFn,
			addCommentInCache,
			createObjectCommentMutationFn,
			experienceTracker,
			isReactionsEnabled,
			pageId,
			pageMode,
			pageType,
		],
	);

	const commentEditorCommentType = commentType === CommentType.GENERAL ? 'page' : commentType;
	const commentEditorPageMode = pageMode === 'edit' ? 'edit' : 'view';

	const editorContainer = (
		<Box
			xcss={
				!expValEquals('confluence_comments_tuneup', 'isEnabled', true) && commentEditorWrapperStyles
			}
			id={replyEditorContainerId}
			testId={replyEditorContainerId}
		>
			<CommentEditor
				pageId={pageId}
				pageType={pageType}
				content={getAdfForQuickReplyMention(replyToCommentUser)}
				appearance="comment"
				EditorComponent={AkCommentEditor}
				onSaveComment={handleSaveComment}
				onContentChange={onChange}
				onEditorReady={onEditorReady}
				commentMode="reply"
				commentType={commentEditorCommentType}
				spaceId={spaceId}
				showCancelButton
				useNewWarningModal
				hideWatchCheckbox
				expandEditor
				pageMode={commentEditorPageMode}
				hasMediaUploadPermissions={hasMediaUploadPermissions}
				topLevelCommentId={parentComment.id}
				shouldWarnOnInternalNavigation
				onCancelComment={handleCancelComment}
				isCommentsPanel={false}
				isFooterComment
				wasRemovedByAnotherUser={parentComment.wasRemovedByAnotherUser}
				isOffline={isOffline}
			/>
		</Box>
	);

	if (!replyToCommentId) {
		return null;
	}

	return expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
		editorContainer
	) : (
		<WithCurvedBranchingStyle
			showDefaultStyles
			customStyles={{
				...commentEditorCustomStyles,
				...(customBeforeLeftForNestedReply && { left: '-25px' }),
			}}
		>
			{editorContainer}
		</WithCurvedBranchingStyle>
	);
};
